import {
  Button,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import { questionLabel } from "./jevCallClient";
import { JevCallDetail } from "./JevCallDetail";
import { useJevTick, useJevTickPage } from "./jevCallQueries";
import { isDropped, tickSteps, verdictText, verdictTone } from "./jevVerdict";
import { clinicClock, duration, percent } from "./traceFormat";
import type { JevTickBody, JevTickCount, JevTickSummary } from "./types";

const tickKey = (tick: JevTickSummary) => `${tick.room_uuid}@${tick.ticked_at}`;

const Gate = ({
  label,
  value,
  dropped,
}: {
  label: string;
  value: number | null | undefined;
  dropped: boolean;
}) =>
  value == null ? null : (
    <span
      className={`rounded-sm px-1.5 font-mono text-xs ${verdictTone(dropped)}`}
    >
      {label} {value.toFixed(2)}
    </span>
  );

const Strip = ({ tick }: { tick: JevTickSummary }) => (
  <span className="flex min-w-0 flex-wrap items-center gap-1 font-mono text-xs">
    {tickSteps(tick).map((step, i) => (
      <span key={step.label + i} className="flex items-center gap-1">
        {i > 0 && (
          <span aria-hidden="true" className="text-rule-strong">
            →
          </span>
        )}
        <span className="rounded-sm border border-rule bg-paper px-1.5 py-0.5 whitespace-nowrap">
          <span className="mr-1 font-body font-medium text-ink">
            {step.label}
          </span>
          {step.value}
        </span>
      </span>
    ))}
  </span>
);

const BodyCard = ({ body }: { body: JevTickBody }) => {
  const dropped = isDropped(body.drop);
  return (
    <article
      className={`grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3 rounded-paper border p-3 ${dropped ? "border-dashed border-rule-strong bg-paper" : "border-rule bg-card"}`}
    >
      {body.crop_url ? (
        <img
          src={body.crop_url}
          alt={`${body.box_key} crop`}
          className="h-24 w-18 rounded-sm border border-rule bg-wall object-contain"
        />
      ) : (
        <span className="grid h-24 w-18 place-items-center rounded-sm border border-rule bg-wall text-center font-mono text-fine-print text-annotation">
          not cropped
        </span>
      )}
      <div className="min-w-0 space-y-1.5 text-xs">
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-mono font-medium text-ink">{body.box_key}</span>
          <span className="text-annotation">step {body.step}</span>
          <span
            className={`rounded-sm px-1.5 font-mono text-fine-print ${verdictTone(dropped)}`}
          >
            {dropped ? "DROPPED" : `KEPT · mark ${body.mark}`}
          </span>
        </p>
        {dropped && body.drop && (
          <p className="text-ink-declined">
            {verdictText(body.drop, body.merged_into)}
          </p>
        )}
        <p className="flex flex-wrap gap-1">
          <Gate label="box" value={body.confidence} dropped={false} />
          <Gate
            label="present"
            value={body.presence}
            dropped={body.drop?.rule === "presence"}
          />
        </p>
        {body.roster && (
          <p>
            <span className="text-annotation">roster</span>{" "}
            <span className="font-mono text-ink">{body.roster}</span>{" "}
            {body.roster_p != null && percent(body.roster_p)}
          </p>
        )}
      </div>
    </article>
  );
};

const region = (edges: JevTickCount["region"]) =>
  edges.map((edge) => Math.round(edge * 100)).join(" ");

/** A quadtree tick's crop counts, one row per crop, kinds side by side. */
const CountTree = ({ counts }: { counts: JevTickCount[] }) => {
  const crops = new Map<string, JevTickCount[]>();
  for (const count of counts)
    crops.set(count.ref, [...(crops.get(count.ref) ?? []), count]);
  return (
    <ul className="space-y-1 font-mono text-xs">
      {[...crops.entries()].map(([ref, kinds]) => (
        <li
          key={ref}
          className="flex flex-wrap gap-2"
          style={{ paddingLeft: `${kinds[0].depth}rem` }}
        >
          <span className="text-annotation">d{kinds[0].depth}</span>
          <span className="text-ink">{ref}</span>
          <span className="text-annotation">[{region(kinds[0].region)}]%</span>
          {kinds.map((k) => (
            <span key={k.kind}>
              {k.kind} {k.count}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
};

const TickCall = ({
  call,
  active,
}: {
  call: JevTickSummary["calls"][number];
  active: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-paper border border-rule bg-card">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full flex-wrap items-baseline gap-3 px-4 py-2.5 text-left hover:bg-smudge focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span aria-hidden="true" className="font-mono text-sm text-annotation">
          {open ? "⌄" : "›"}
        </span>
        <span className="text-sm font-medium text-ink">
          {questionLabel(call.question)}
        </span>
        <span className="font-mono text-xs text-annotation">
          {call.error ? `Failed · ${call.error}` : duration(call.ms)}
        </span>
      </button>
      {open && <JevCallDetail callUuid={call.uuid} active={active} />}
    </li>
  );
};

const TickDetail = ({
  tick,
  active,
}: {
  tick: JevTickSummary;
  active: boolean;
}) => {
  const query = useJevTick(tick.room_uuid, tick.ticked_at, active);
  const shown = query.data ?? tick;
  return (
    <div className="space-y-4 border-t border-rule bg-wall p-4 sm:p-5">
      {shown.counts.length > 0 && (
        <section className="space-y-2">
          <p className="text-mono-caps text-xs text-annotation">
            Crops this tick counted
          </p>
          <CountTree counts={shown.counts} />
        </section>
      )}
      {shown.bodies.length > 0 && (
        <section className="space-y-2">
          <p className="text-mono-caps text-xs text-annotation">
            Boxes this tick drew
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shown.bodies.map((body) => (
              <BodyCard key={`${body.step}:${body.box_key}`} body={body} />
            ))}
          </div>
          {query.isError && (
            <p className="text-xs text-annotation">
              Crops could not be loaded.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void query.refetch()}
              >
                Try again
              </button>
            </p>
          )}
        </section>
      )}
      <section className="space-y-2">
        <p className="text-mono-caps text-xs text-annotation">
          Calls in this tick
        </p>
        <ul className="space-y-2">
          {tick.calls.map((call) => (
            <TickCall key={call.uuid} call={call} active={active} />
          ))}
        </ul>
      </section>
    </div>
  );
};

const TickRow = ({
  tick,
  timezone,
  expanded,
  active,
  onToggle,
}: {
  tick: JevTickSummary;
  timezone: string;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
}) => {
  const failed = tick.calls.filter((c) => c.error).length;
  return (
    <li
      className="border-b border-rule last:border-b-0"
      data-dd-privacy="hidden"
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left hover:bg-smudge focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent sm:grid-cols-[auto_minmax(9rem,1fr)_minmax(8rem,1fr)_minmax(16rem,4fr)_auto] sm:gap-4"
      >
        <span aria-hidden="true" className="font-mono text-sm text-annotation">
          {expanded ? "⌄" : "›"}
        </span>
        <time
          dateTime={tick.ticked_at}
          className="block text-mono-time text-xs text-ink-body"
        >
          {clinicClock(tick.ticked_at, timezone)}
        </time>
        <span className="hidden min-w-0 sm:block">
          <span className="block truncate text-sm font-medium text-ink">
            {tick.room_name}
          </span>
          <span className="mt-1 block truncate text-xs text-annotation">
            {tick.calls.length} calls
            {failed > 0 && ` · ${failed} failed`}
          </span>
        </span>
        <span className="col-span-2 col-start-2 sm:col-span-1 sm:col-start-auto">
          <span className="mb-1 block text-xs text-annotation sm:hidden">
            {tick.room_name} · {tick.calls.length} calls
          </span>
          <Strip tick={tick} />
        </span>
        <span className="row-start-1 text-right font-mono text-xs text-annotation sm:row-start-auto">
          {duration(tick.ms)}
        </span>
      </button>
      {expanded && <TickDetail tick={tick} active={active} />}
    </li>
  );
};

/** Ticks newest first, each the run of calls one room tick made. Its parent
 * keys it by room and refresh, so either starts over from the first page. */
export const JevTicksPanel = ({
  active,
  roomUuid,
}: {
  active: boolean;
  roomUuid: string;
}) => {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const goTo = (next: (string | undefined)[]) => {
    setCursors(next);
    setExpanded(new Set());
  };
  const query = useJevTickPage(active, roomUuid, cursors[cursors.length - 1]);
  const rows = query.data?.items ?? [];
  const timezone = query.data?.clinic_timezone || "UTC";
  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const nextCursor = query.data?.next_cursor;
  const busy = !active || query.isFetching;
  return (
    <>
      <div className="overflow-hidden rounded-paper border border-rule bg-card">
        <div className="hidden grid-cols-[auto_minmax(9rem,1fr)_minmax(8rem,1fr)_minmax(16rem,4fr)_auto] gap-4 border-b border-rule px-4 py-2 text-annotation-caps text-annotation sm:grid">
          <span />
          <span>Time</span>
          <span>Room</span>
          <span>What happened</span>
          <span>Took</span>
        </div>
        {query.isPending && <SkeletonStack count={5} className="p-5" />}
        {query.isError && (
          <PanelError
            label="Jev ticks"
            onRetry={() => void query.refetch()}
            className="min-h-48"
          />
        )}
        {query.data && rows.length === 0 && (
          <EmptyNote>No recorded ticks match this view.</EmptyNote>
        )}
        {rows.length > 0 && (
          <ul>
            {rows.map((tick) => (
              <TickRow
                key={tickKey(tick)}
                tick={tick}
                timezone={timezone}
                expanded={expanded.has(tickKey(tick))}
                active={active}
                onToggle={() => toggle(tickKey(tick))}
              />
            ))}
          </ul>
        )}
      </div>
      {query.data && (
        <nav className="flex items-center gap-3" aria-label="Jev tick pages">
          <Button
            variant="outline"
            size="sm"
            disabled={busy || cursors.length === 1}
            onClick={() => goTo(cursors.slice(0, -1))}
          >
            Previous
          </Button>
          <span className="text-sm text-ink-body">Page {cursors.length}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={busy || !nextCursor}
            onClick={() => nextCursor && goTo([...cursors, nextCursor])}
          >
            Next
          </Button>
        </nav>
      )}
    </>
  );
};
